import { useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/hooks/redux'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser, faLock } from '@fortawesome/free-solid-svg-icons'
import api from '@/services/api'
import { updatePreferences } from '@/store/slices/authSlice'

/**
 * SuperAdmin Settings Page - Manage profile and password
 * Dark mode geek aesthetic - Desktop only
 */
export default function SuperAdminSettingsPage() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const user = useAppSelector((state) => state.auth.user)

  // Profile form state - initialized directly from user, no useEffect needed
  const [firstName, setFirstName] = useState(user?.profile?.firstName || '')
  const [lastName, setLastName] = useState(user?.profile?.lastName || '')
  const [pseudo, setPseudo] = useState(user?.profile?.pseudo || '')
  const [email, setEmail] = useState(user?.email || '')
  const [profileSaving, setProfileSaving] = useState(false)
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Password reset state
  const [passwordSaving, setPasswordSaving] = useState(false)
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const handleSaveProfile = async () => {
    try {
      setProfileSaving(true)
      setProfileMessage(null)

      // Use Redux thunk to update preferences and sync Redux state
      await dispatch(updatePreferences({
        profile: {
          firstName,
          lastName,
          pseudo,
        },
        ...(email !== user?.email && { email }),
      })).unwrap()

      setProfileMessage({ type: 'success', text: t('superadmin.profileUpdated') })
    } catch (error: any) {
      setProfileMessage({
        type: 'error',
        text: error || t('common.error'),
      })
    } finally {
      setProfileSaving(false)
    }
  }

  const handleChangePassword = async () => {
    try {
      setPasswordSaving(true)
      setPasswordMessage(null)

      // Send password reset email link
      const response = await api.post('/auth/forgot-password', {
        email: user?.email,
      })

      if (response.data.success) {
        setPasswordMessage({ 
          type: 'success', 
          text: t('superadmin.passwordUpdated') + ' - ' + t('email.checkInbox') || 'Check your email for the password reset link'
        })
      }
    } catch (error: any) {
      setPasswordMessage({
        type: 'error',
        text: error.response?.data?.message || t('common.error'),
      })
    } finally {
      setPasswordSaving(false)
    }
  }

  return (
    <div className="space-y-8 w-2/3 mx-auto">
      {/* Page Header */}
      <div>
        <h1 className="text-4xl font-black bg-linear-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
          {t('superadmin.settings')}
        </h1>
        <p className="text-slate-400 mt-2 font-mono text-sm">
          {'> '} <span className="text-cyan-400">manage</span>{' --profile'}
        </p>
      </div>

      {/* Profile Section */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-linear-to-br from-cyan-600 to-cyan-500 rounded-lg">
            <FontAwesomeIcon icon={faUser} className="text-white text-lg" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">{t('superadmin.editProfile')}</h2>
            <p className="text-slate-400 text-sm">{t('superadmin.profileDescription')}</p>
          </div>
        </div>

        {profileMessage && (
          <div className={`mb-6 p-4 rounded-lg border ${
            profileMessage.type === 'success'
              ? 'bg-green-900/30 border-green-500/50 text-green-300'
              : 'bg-red-900/30 border-red-500/50 text-red-300'
          }`}>
            <p className="font-mono text-sm">
              <span className={profileMessage.type === 'success' ? 'text-green-500' : 'text-red-500'}>
                {profileMessage.type === 'success' ? '[SUCCESS]' : '[ERROR]'}
              </span> {profileMessage.text}
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
              {t('common.firstName')}
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={profileSaving}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 transition disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
              {t('common.lastName')}
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={profileSaving}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 transition disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
              {t('common.pseudo')}
            </label>
            <input
              type="text"
              value={pseudo}
              onChange={(e) => setPseudo(e.target.value)}
              disabled={profileSaving}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 transition disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-xs font-mono font-bold text-cyan-400 mb-2 uppercase tracking-wider">
              {t('common.email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={profileSaving}
              autoComplete="email"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 font-mono text-sm focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/30 transition disabled:opacity-50"
            />
          </div>

          {user?.pendingEmail && (
            <div className="p-3 bg-purple-900/30 border border-purple-500/50 rounded-lg">
              <p className="text-xs text-purple-300 font-mono">
                <span className="text-purple-400">{'[PENDING]'}</span> {t('superadmin.pendingEmail')}: <span className="text-purple-200">{user.pendingEmail}</span>
              </p>
              <p className="text-xs text-purple-300 font-mono mt-1">
                {t('superadmin.emailChangeConfirm')}
              </p>
            </div>
          )}

          <button
            onClick={handleSaveProfile}
            disabled={profileSaving}
            className="w-full px-4 py-3 bg-linear-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-lg transition duration-200 text-sm uppercase tracking-wider"
          >
            {profileSaving ? '⟳ ' + t('common.loading') : t('common.save')}
          </button>
        </div>
      </div>

      {/* Password Section */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-linear-to-br from-purple-600 to-purple-500 rounded-lg">
            <FontAwesomeIcon icon={faLock} className="text-white text-lg" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">{t('superadmin.changePassword')}</h2>
            <p className="text-slate-400 text-sm">{t('superadmin.passwordDescription')}</p>
          </div>
        </div>

        {passwordMessage && (
          <div className={`mb-6 p-4 rounded-lg border ${
            passwordMessage.type === 'success'
              ? 'bg-green-900/30 border-green-500/50 text-green-300'
              : 'bg-red-900/30 border-red-500/50 text-red-300'
          }`}>
            <p className="font-mono text-sm">
              <span className={passwordMessage.type === 'success' ? 'text-green-500' : 'text-red-500'}>
                {passwordMessage.type === 'success' ? '[SUCCESS]' : '[ERROR]'}
              </span> {passwordMessage.text}
            </p>
          </div>
        )}

         <div className="space-y-4">
           <p className="text-slate-300 text-sm">
             {t('superadmin.passwordDescription')}
           </p>

           <button
             onClick={handleChangePassword}
             disabled={passwordSaving}
             className="w-full px-4 py-3 bg-linear-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-lg transition duration-200 text-sm uppercase tracking-wider"
           >
             {passwordSaving ? '⟳ ' + t('common.loading') : t('superadmin.sendResetLink')}
           </button>
         </div>
      </div>
    </div>
  )
}

